import { Component } from '@theme/component';

/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 8;

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productHandle - The handle of the product to add.
   */
  static addProduct(productHandle) {
    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ handle) => handle !== productHandle);
    viewedProducts.unshift(productHandle);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
  }

  /**
   * Retrieves the list of recently viewed products from session storage.
   * @returns {string[]} The list of viewed product handles.
   */
  static getProducts() {
    const stored = JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');

    // Filter out any invalid entries (like variant IDs that are all numbers)
    // Product handles should contain dashes or letters
    const validProducts = stored.filter((handle) => {
      // If it's a pure number (variant ID), skip it
      if (/^\d+$/.test(handle)) return false;
      return true;
    });

    // If we filtered anything out, update storage
    if (validProducts.length !== stored.length) {
      localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(validProducts));
    }

    return validProducts;
  }
}

/**
 * Custom element for displaying recently viewed products
 */
class RecentlyViewedProductsElement extends Component {
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#init();
  }

  async #init() {
    const currentProductHandle = this.dataset.productHandle;

    // Track current product
    if (currentProductHandle) {
      RecentlyViewed.addProduct(currentProductHandle);
    }

    // Get recently viewed products (excluding current)
    let recentProductHandles = RecentlyViewed.getProducts().filter(
      (handle) => handle !== currentProductHandle
    );

    // Limit to max products setting
    const maxProducts = parseInt(this.dataset.maxProducts || '4');
    recentProductHandles = recentProductHandles.slice(0, maxProducts);

    // If no products, hide section
    if (recentProductHandles.length === 0) {
      this.classList.add('hidden');
      this.dataset.hasProducts = 'false';
      return;
    }

    this.dataset.hasProducts = 'true';

    // Load products
    await this.#loadProducts(recentProductHandles);
  }

  async #loadProducts(productHandles) {
    try {
      this.#activeFetch?.abort();
      this.#activeFetch = new AbortController();

      // Fetch product data for each handle using the .js endpoint
      const productPromises = productHandles.map((handle) =>
        fetch(`/products/${handle}.js`, { signal: this.#activeFetch.signal })
          .then((res) => (res.ok ? res.json() : null))
          .catch(() => null)
      );

      const products = (await Promise.all(productPromises)).filter((p) => p !== null);

      if (products.length === 0) {
        this.classList.add('hidden');
        this.dataset.hasProducts = 'false';
        return;
      }

      // Render products
      await this.#renderProducts(products);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error loading recently viewed products:', error);
        this.classList.add('hidden');
        this.dataset.hasProducts = 'false';
      }
    } finally {
      this.#activeFetch = null;
    }
  }

  async #renderProducts(products) {
    const container = this.querySelector('[data-skeleton]');
    if (!container) return;

    // Create new content container
    const newContainer = document.createElement('div');
    newContainer.className = container.className;
    newContainer.removeAttribute('data-skeleton');

    // Render each product card
    for (const product of products) {
      const productCard = this.#createProductCard(product);
      const wrapper = document.createElement('div');
      wrapper.className = 'resource-list__item';
      wrapper.appendChild(productCard);
      newContainer.appendChild(wrapper);
    }

    // Replace skeleton with new content
    container.parentNode.replaceChild(newContainer, container);
  }

  #createProductCard(product) {
    const card = document.createElement('div');
    card.className = 'product-card';
    card.setAttribute('data-product-id', product.id);
    card.style.cssText = 'display: block; height: 100%;';

    const image = product.featured_image || product.images?.[0];

    // Format price using Shopify's money format if available
    let formattedPrice = `€${(product.price / 100).toFixed(2)}`;
    if (window.Shopify?.formatMoney) {
      const moneyFormat = window.theme?.moneyFormat || '€{{amount}}';
      formattedPrice = Shopify.formatMoney(product.price, moneyFormat);
    }

    // Build image HTML with proper sizing
    const imageHtml = image
      ? `<img
          src="${image}"
          alt="${this.#escapeHtml(product.title)}"
          loading="lazy"
          style="width: 100%; height: auto; display: block; object-fit: cover; aspect-ratio: 3/4;"
        />`
      : '<div style="width: 100%; aspect-ratio: 3/4; background: #f0f0f0;"></div>';

    card.innerHTML = `
      <a href="${product.url}" class="product-card__link" style="display: block; height: 100%; text-decoration: none; color: inherit;">
        <div class="product-card__content" style="display: flex; flex-direction: column; gap: 8px; height: 100%;">
          <div class="product-card__media" style="position: relative; overflow: hidden; background: #f5f5f5; flex-shrink: 0;">
            ${imageHtml}
          </div>
          <div class="product-card__info" style="display: flex; flex-direction: column; gap: 4px; padding: 4px 0;">
            <p class="product-card__title" style=" margin: 0; line-height: 1.4; color: #000;">
              ${this.#escapeHtml(product.title)}
            </p>
            <div class="product-card__price" style="color: #000;">
              ${formattedPrice}
            </div>
          </div>
        </div>
      </a>
    `;

    return card;
  }

  #escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProductsElement);
}
